import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scale } from "lucide-react";
import { useT } from "@/i18n";
import BrandMark from "@/components/BrandMark";

export default function ForgotPassword() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <BrandMark className="h-7 w-7" iconClassName="h-6 w-6 text-amber-600" />
          <span className="font-serif text-xl font-semibold text-slate-900">RPLAW</span>
        </div>
        {done ? (
          <div data-testid="forgot-password-confirmation">
            <h2 className="font-serif text-2xl text-slate-900">{t("auth.check_inbox")}</h2>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              {t("auth.reset_sent")}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-password-form">
            <h2 className="font-serif text-2xl text-slate-900">{t("auth.reset_title")}</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">
              {t("auth.reset_body")}
            </p>
            {error && (
              <div data-testid="forgot-password-error" className="mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Label htmlFor="fp-email">{t("auth.email")}</Label>
            <Input
              id="fp-email"
              data-testid="forgot-password-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5"
            />
            <Button
              data-testid="forgot-password-submit-button"
              type="submit"
              disabled={loading}
              className="w-full mt-6 bg-slate-900 hover:bg-slate-800"
            >
              {loading ? t("auth.sending") : t("auth.send_link")}
            </Button>
          </form>
        )}
        <div className="mt-4 text-center">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
          >
            {t("auth.back_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
